/**
 * Who may READ a contract and ask about it -- the client's copy.
 *
 * Mirrors OFFICE_CONTRACT_READER_ROLES in functions-shared/src/contracts/officeContractReaders.ts
 * (config/__tests__/contractAccess.test.ts reads that file and fails on any difference). The
 * server is the boundary: contracts_askContract and firestore.rules refuse everyone else. This
 * copy only decides which routes and buttons a user is offered.
 *
 * Decision (Dillan, 2026-10-02): office staff with Field Service access read contracts. Technician
 * and Investor do not. The setup worklists, Re-read, Upload and decisions stay manager-only.
 */

import type { UserRole } from '../types/firestore';

/**
 * The roles that may read contracts, as stored (`users.role` is TitleCase).
 *
 * CASE. The server (contracts_askContract) and firestore.rules compare roles
 * case-insensitively. The client's own role checks mostly do NOT: `ProtectedRoute
 * requiredRole`, nav `requiredRoles` and `useAuth().isManagerOrAdmin()` are exact-match, so a
 * role that is not stored in TitleCase (e.g. "estimator") is LOCKED OUT of any UI gated that
 * way even though the server would serve it. The Contracts routes and nav item therefore
 * gate on `isOfficeContractReader` below (ProtectedRoute `allowRole`, nav `allowRole`), which
 * matches the server. The manager-only sections inside the pages still use the exact-match
 * `isManagerOrAdmin()`, as do the manager rules (`isOwnerOrManager`), so those two agree.
 */
export const OFFICE_CONTRACT_READER_ROLES: readonly UserRole[] = Object.freeze<UserRole[]>([
  'Owner',
  'Admin',
  'Manager',
  'Estimator',
  'Finance',
  'Developer',
]);

const LOWERED: ReadonlySet<string> = new Set(OFFICE_CONTRACT_READER_ROLES.map((r) => r.toLowerCase()));

/**
 * Whether a role may read contracts. Case-insensitive; a non-string role is refused.
 * @param {unknown} role The user's role.
 * @returns {boolean} True for one of OFFICE_CONTRACT_READER_ROLES.
 */
export function isOfficeContractReader(role: unknown): boolean {
  return typeof role === 'string' && LOWERED.has(role.toLowerCase());
}
